<template>
  <StagedPreselectBlock
    :title="title"
    :fields="fields"
    :color-schema="colorSchema"
    :show-full-width="showFullWidth"
    :settings="settings"
    :staged="staged"
  />
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted } from 'vue';

import { generateUrl } from '@shop/utils/Routing';
import i18n, { useTranslation } from '@shop/utils/Translation';

import StagedPreselectBlock from './StagedPreselectBlock.vue';
import { useStagedPreselect } from './useStagedPreselect';

import type { RoofRackPreselectBlockProps } from './RoofRackPreselectBlock.types';
import type { PreselectOption } from './StagedPreselectBlock.types';

const props = defineProps<RoofRackPreselectBlockProps>();

useTranslation('roofrack_tool');

const attributes = computed(() => props.fields.map(field => field.name));

const initialOptions = computed(() => {
  const opts: Record<string, PreselectOption[]> = {};
  props.fields.forEach(field => {
    if (field.options?.length) {
      opts[field.name] = field.options;
    }
  });
  return opts;
});

const transformRoofValue = async (attribute: string, value: string): Promise<string> => {
  if (attribute !== 'roof' || !Number.isNaN(Number(value))) {
    return value;
  }

  const words = value.split('-');
  const translatedWords = words.map(word =>
    i18n.global.t(`words.roof.${word}`, {}, { default: word })
  );
  const translatedValue = translatedWords.join(' ').toLowerCase();

  const roofOptions = staged.availableOptions.roof ?? [];
  const match = roofOptions.find(option => {
    if (typeof option === 'object' && option !== null && 'value' in option) {
      return option.value.toLowerCase() === translatedValue;
    }
    return false;
  });

  if (typeof match === 'object' && match !== null && 'id' in match) {
    return String(match.id);
  }

  return value;
};

const staged = useStagedPreselect({
  attributes: attributes.value,
  optionsRoute: props.optionsRoute,
  initialOptions: initialOptions.value,
  settings: props.settings,
  transformValue: transformRoofValue,
  onComplete: async () => {
    const roofId = staged.selectedOptions.roof;
    const roofOptions = staged.availableOptions.roof ?? [];
    const roof = roofOptions.find(option => {
      if (typeof option === 'object' && option !== null && 'id' in option) {
        return String(option.id) === roofId;
      }
      return false;
    });

    if (!roof) {
      staged.loading.value = false;
      return;
    }

    const id =
      typeof roof === 'object' && roof !== null && 'id' in roof
        ? roof.id
        : staged.selectedOptions.roof;

    try {
      const response = await axios.get<{ url?: string }>(
        generateUrl('category_tool_roofrack_url', { car_id: id })
      );
      if (response.data.url) {
        window.location.href = response.data.url;
        return;
      }
    } catch {
      // fall through
    }

    staged.loading.value = false;
  },
});

onMounted(() => {
  staged.loadFromUrlParams(new URL(window.location.href).searchParams, {
    brand: 'brand',
    model: 'model',
    type_year: 'type_year',
    roof: 'roof',
  });
});
</script>
